// Insights for the selected node, on the tree page rather than a page away.
//
// This used to be two places at once. A model level Insights tab, and a per node
// univariate page reached by a link that navigated off the tree. The two were
// the same thing: `getUnivariateForANodeTotal` falls straight through to
// `getUnivariateTotal` when a node has no conditions, so the tab was the root
// node's insights under a different name. Merging them and docking the result
// under the tree is what makes the loop worth having, because reading an
// attribute and splitting on it stop being separate pages.
//
// The fetch is deliberately tied to selection rather than to hover. A cache miss
// on the per node univariate rebuilds a filtered table, and that cache is
// dropped whenever the tree is written, so the very loop this panel exists for
// is the one that misses. Firing it for every node a cursor crosses would be
// unkind to both the user and the database.
import { inject, observer } from 'mobx-react';
import React from 'react';
import { Alert } from 'react-bootstrap';
import { Condition } from '../../../models/sqlform';
import UnivariateAnalysisVariable from '../../../models/univariate/UnivariateAnalysisVariable';
import Store from '../../../store';
import Spinner from '../Spinner';
import Error from '../error/Error';
import { ConditionTrait, TraitPart } from '../trees/conditionText';
import BucketLadder from './BucketLadder';
import {
  analyseAttribute,
  AttributeStats,
  MIN_SPLIT_POPULATION,
  NodeTotals,
  SplitCandidate,
} from './bucketStats';
import { descriptionOf, labelOf } from './computationDictionary';
import ComputationRail, {
  RailSort,
  rankAttributes,
  RankedAttribute,
} from './ComputationRail';
import SplitConfirm from './SplitConfirm';
import { bucketSplitPreview } from './splitPreview';
import './nodeInsights.css';

interface NodeInsightsPanelProps {
  store?: Store;
  treeId: number;
  nodeId: number;
  /** The node's own figures, from the tree. Never derived from the buckets. */
  node: NodeTotals;
  /**
   * Distance from the root, the root being 0. Read off the structure by the page.
   *
   * Only the coverage rule uses it. Omitted, that rule does not fire, which is the right
   * failure: a column that conflates missing data stays on offer rather than vanishing
   * from a panel that had no way to know where it was standing.
   */
  depth?: number;
  /**
   * The node's path condition, from the structure the page already holds.
   * Passed in rather than looked up so that fetching cannot reload the tree:
   * `TreesPageModel.loadOne` blanks `tree.definition` before fetching, and this
   * panel is rendered inside a `tree.definition` guard, so a self-triggered
   * reload unmounts it, remounts it, and starts again.
   */
  conditions: string;
  hideUnknown: boolean;
  /**
   * Split the node on a confirmed proposal. The page writes it straight to the
   * tree; nothing has to be selected or opened first. Same path the AI diagnostics
   * panel uses.
   */
  onApplySplit?: (
    nodeId: number,
    conditions: Condition[]
  ) => Promise<void> | void;
}

interface NodeInsightsPanelState {
  selectedId: string;
  search: string;
  sort: RailSort;
  expanded: boolean;
  /** Matched rows of the proposed split, while it is being hovered. */
  highlight: number[] | null;
  /**
   * The attribute whose split is being confirmed, rather than a flag, so choosing
   * another attribute closes a confirm that is no longer about what is on screen.
   */
  confirming: string | null;
  /** True from Apply until the tree has been written. */
  applying: boolean;
}

const count = (n: number) => n.toLocaleString('en-US');
const pct = (n: number, digits = 1) => `${(n * 100).toFixed(digits)}%`;

/**
 * The proposal in the panel's own words, as a trait for the same renderer the
 * node conditions use.
 *
 * The rule here is a bucket-ladder label ("Prospect or better"), not SQL, so
 * there is nothing to hand `conditionTraits`. The ladder is where this English
 * comes from and it is what the reader has just looked at; spelling out the
 * fourteen values behind "or better" would describe the same split worse. The
 * field and the value go over separately because those are the two kinds of word
 * `TraitText` sets apart.
 */
function proposalTrait(attributeName: string, label: string): ConditionTrait {
  const parts: TraitPart[] = [
    { kind: 'field', text: attributeName },
    { kind: 'value', text: label },
  ];
  return { text: `${attributeName} ${label}`, parts };
}

export default inject('store')(
  observer(
    class NodeInsightsPanel extends React.Component<
      NodeInsightsPanelProps,
      NodeInsightsPanelState
    > {
      constructor(props: NodeInsightsPanelProps) {
        super(props);
        this.state = {
          selectedId: '',
          search: '',
          sort: 'best',
          expanded: false,
          highlight: null,
          confirming: null,
          applying: false,
        };
        this.select = this.select.bind(this);
        this.applySplit = this.applySplit.bind(this);
      }

      /** The node this component last issued a request for. */
      requested: string = null;

      /**
       * Whether this is still on screen. Saving a split reloads the tree, which
       * blanks `tree.definition`, and this panel is rendered inside a guard on it,
       * so the successful path is the one that unmounts it mid-await.
       */
      mounted = false;

      componentDidMount() {
        this.mounted = true;
        this.load();
      }

      componentWillUnmount() {
        this.mounted = false;
      }

      componentDidUpdate(previous: NodeInsightsPanelProps) {
        if (
          previous.nodeId !== this.props.nodeId ||
          previous.treeId !== this.props.treeId ||
          previous.conditions !== this.props.conditions
        ) {
          // A different node is a different question, so the chosen attribute,
          // any expansion and any open confirm go with it.
          this.setState({ selectedId: '', expanded: false, confirming: null });
          this.load();
        }
      }

      /**
       * Guarded against asking twice for the same node. A remount, a store
       * update or a re-render must not re-issue the request: a miss rebuilds a
       * filtered table server side, and that cache is dropped on every tree
       * write, so repeats are not cheap.
       */
      /**
       * Every row the split does not match, which is what "everything else"
       * means. Includes a hidden unknown bucket, since that is genuinely where
       * those leads go.
       */
      // eslint-disable-next-line class-methods-use-this
      complementRows(stats: AttributeStats, candidate: SplitCandidate) {
        const matched = new Set(candidate.matchedRows);
        return stats.rows
          .map((_, index) => index)
          .filter((index) => !matched.has(index));
      }

      /**
       * The proposal as a condition the tree can hold, or null when the model has
       * no form field for the attribute, which is what hides the button.
       *
       * Every candidate this panel makes is a set of buckets, so the verb is always
       * membership, in whichever direction `bucketStats` chose to state the rule.
       */
      splitCondition(
        candidate: SplitCandidate,
        attributeId: string
      ): Condition | null {
        const subject = this.props.store.formFields?.find(
          (field) => field.subject === attributeId
        );
        if (!subject) return null;
        return {
          subject,
          verb: candidate.negated ? 'is none of' : 'is any of',
          complements: [...candidate.matchedValues],
          lower: true,
        };
      }

      /**
       * Write the confirmed split.
       *
       * Awaited so the button can say what is happening, and guarded on `mounted`
       * because saving reloads the tree and unmounts this panel on the way.
       */
      async applySplit(candidate: SplitCandidate, attributeId: string) {
        const { nodeId, onApplySplit } = this.props;
        const condition = this.splitCondition(candidate, attributeId);
        if (!condition || !onApplySplit || this.state.applying) return;
        this.setState({ applying: true });
        try {
          // Wrapped rather than sent bare: the shared handler ANDs whatever it is
          // given, because the diagnostics panel can propose a cut over four columns.
          // Every candidate THIS panel makes is a set of buckets on one attribute, so
          // it is always exactly one.
          await onApplySplit(Number(nodeId), [condition]);
        } finally {
          if (this.mounted) {
            this.setState({ applying: false, confirming: null });
          }
        }
      }

      load() {
        const { store, treeId, nodeId, conditions } = this.props;
        // The split form needs these to build a condition from a proposal.
        store.fetchFormFields();
        const key = `${treeId}/${nodeId}/${conditions ?? ''}`;
        if (this.requested === key) return;
        this.requested = key;
        // Every lead, personal emails included. The node figures this panel
        // measures against come from the tree, which counts every row, so a
        // filtered fetch left the personal leads in the baseline and out of every
        // bucket. On one customer's root node the buckets summed to 1,815 against a
        // node of 2,165, both values of a 0/1 column read above average, and the
        // recommended split was those 350 leads against the rest. The tree splits
        // every row too, so this is also what a split from here acts on.
        store.loadNodeUnivariate(
          Number(treeId),
          Number(nodeId),
          conditions,
          true
        );
      }

      select(selectedId: string) {
        this.setState({ selectedId, expanded: false });
      }

      /** One analysis per attribute, so the rail can rank on the same figures. */
      analysed(): RankedAttribute[] {
        const { store, node, hideUnknown, depth } = this.props;
        const variables: UnivariateAnalysisVariable[] =
          store.nodePage.univariateAnalysis?.variables ?? [];
        return rankAttributes(
          variables.map((variable) => ({
            id: variable.id,
            // `variableName` is `computationDisplayName(subject)`, which splits the column
            // on underscores and capitalises each part. That is what put "It Spend Bucket",
            // "Hg Hqcountry" and "Tag Is B2b Enterprise" in front of customers. The
            // dictionary's label was written for a marketer and reviewed, so it wins where
            // it exists and the derived name stands in where it does not.
            //
            // Applied HERE and not upstream on purpose: `convertIntToYesNo` builds a
            // boolean's BUCKET labels by matching /^Tag Is (.*)/, /^Has (.*)/ and /^Is (.*)/
            // against this same display name, so overwriting it server-side would silently
            // degrade every flag's buckets to Yes/No.
            name: labelOf(variable.id) ?? variable.variableName,
            description: descriptionOf(variable.id),
            // `variable.id` is the column, which is how the computation dictionary is
            // keyed. Without it the analysis falls back to inferring shape from bucket
            // coverage and cannot know a scale's order.
            stats: analyseAttribute(variable.buckets, node, {
              hideUnknown,
              column: variable.id,
              depth,
            }),
          })),
          this.state.sort
        );
      }

      /**
       * The best split, stated and left at that. This was a bordered callout
       * with a strength adjective and a "removes N% of this node's uncertainty"
       * headline. The figure is real but it is not a sentence anyone reasons
       * with, and dressing a summary as an alert implies the page has an
       * opinion. Two rows of numbers in the same visual language as the table
       * below say the same thing and leave the reading to whoever is splitting.
       */
      renderVerdict(attribute: RankedAttribute) {
        const { id: attributeId, stats } = attribute;
        const { best } = stats;
        // Two different silences. "Nothing clears the minimums" is a statement about this
        // node's numbers; an ineligible column has no candidates for a reason that has
        // nothing to do with the numbers, and saying the former about it is simply untrue.
        if (stats.ineligible) {
          return (
            <p className="nins-none">
              This is not a field to split on: {stats.ineligible}. Its
              distribution is shown so you can read it, but a tree built on it
              would not mean anything.
            </p>
          );
        }
        // A third silence, and the one most easily misread. The field is fine; this
        // place in the tree is not. Saying "not a field to split on" here sends
        // somebody to look for a problem with a column that has none.
        if (stats.ineligibleHere) {
          return (
            <p className="nins-none">
              Not a split to make this close to the root: {stats.ineligibleHere}
              . Further down the tree it is offered normally.
            </p>
          );
        }
        if (stats.tooManyValues) {
          return (
            <p className="nins-none">
              Too many values to split on:{' '}
              {stats.tooManyValues.toLocaleString()}.
            </p>
          );
        }
        if (!best) {
          return (
            <p className="nins-none">
              No split on this attribute clears the minimums: both sides need{' '}
              {MIN_SPLIT_POPULATION} leads and the matched side has to convert
              better than the node.
            </p>
          );
        }
        const rate = (value: number) =>
          stats.baseline ? `${(value / stats.baseline).toFixed(2)}x` : '';
        const canSplit = !!this.splitCondition(best, attributeId);
        const confirming = canSplit && this.state.confirming === attributeId;
        return (
          <div className="nins-best">
            <div className="nins-best-label">
              Recommended split
              {canSplit && !confirming && (
                <button
                  type="button"
                  className="nins-create"
                  onClick={() => this.setState({ confirming: attributeId })}
                >
                  Create this split
                </button>
              )}
            </div>
            {/* Hovering the proposal lights the buckets it covers in the table
                below, because a rule like "Prospect or better" does not say
                which those are. */}
            <div className="nins-sides">
              <div
                className="nins-side nins-side--pos"
                onMouseEnter={() =>
                  this.setState({ highlight: best.matchedRows })
                }
                onMouseLeave={() => this.setState({ highlight: null })}
              >
                <div className="nins-side-rule">{best.label}</div>
                {/* Lift, not the conversion rate. Every other surface leads
                    with lift, and this card led with a percentage that only
                    means something once you have gone and found the node
                    average to divide it by. */}
                <div className="nins-side-rate">{rate(best.matched.rate)}</div>
                <div className="nins-side-meta">
                  {count(best.matched.population)} leads (
                  {pct(best.matched.population / stats.node.population, 0)}),{' '}
                  {count(best.matched.conversions)} conversions (
                  {pct(best.matched.conversions / stats.node.conversions, 0)})
                </div>
              </div>
              <div
                className="nins-side nins-side--neg"
                onMouseEnter={() =>
                  this.setState({ highlight: this.complementRows(stats, best) })
                }
                onMouseLeave={() => this.setState({ highlight: null })}
              >
                <div className="nins-side-rule">{best.otherLabel}</div>
                <div className="nins-side-rate">{rate(best.rest.rate)}</div>
                <div className="nins-side-meta">
                  {count(best.rest.population)} leads (
                  {pct(best.rest.population / stats.node.population, 0)}),{' '}
                  {count(best.rest.conversions)} conversions (
                  {pct(best.rest.conversions / stats.node.conversions, 0)})
                </div>
              </div>
            </div>
            {/* Directly under the two sides it restates, so the confirm reads as
                the next step in the card rather than as a second recommendation
                somewhere else on the page. */}
            {confirming && (
              <SplitConfirm
                trait={proposalTrait(attribute.name, best.label)}
                preview={bucketSplitPreview(best, stats.node, stats.baseline)}
                saving={this.state.applying}
                onApply={() => this.applySplit(best, attributeId)}
                onCancel={() => this.setState({ confirming: null })}
              />
            )}
          </div>
        );
      }

      renderNotices(stats: AttributeStats) {
        return (
          <>
            {stats.exceedsNode && (
              <div className="nins-notice nins-notice--alarm">
                <span>&#9888;</span>
                <span>
                  A bucket here is larger than the node itself, which cannot
                  happen with real node data. These are almost certainly
                  whole-model figures served under a node heading, which the
                  endpoint does after a tree is written. Treat every number
                  below as suspect and reload.
                </span>
              </div>
            )}
            {stats.shape === 'multi' && (
              <div className="nins-notice nins-notice--info">
                <span>&#9432;</span>
                <span>
                  Values overlap: {stats.rows.length} of them across{' '}
                  {count(stats.node.population)} leads,{' '}
                  {stats.valuesPerLead.toFixed(1)} per lead on average, so the
                  percentages do not add to 100. A split on this attribute can
                  only test one value at a time.
                </span>
              </div>
            )}
          </>
        );
      }

      render() {
        const { store, nodeId, node } = this.props;
        const { nodePage } = store;

        if (nodePage.univariateAnalysisError) {
          return <Error message={nodePage.univariateAnalysisError} />;
        }
        if (
          nodePage.loadingUnivariateAnalysis ||
          !nodePage.univariateAnalysis
        ) {
          return <Spinner />;
        }

        const attributes = this.analysed();
        if (!attributes.length) {
          return (
            <Alert variant="light">
              No attributes were returned for node {nodeId}.
            </Alert>
          );
        }

        const selected =
          attributes.find((a) => a.id === this.state.selectedId) ??
          attributes[0];
        const { stats } = selected;

        return (
          <div className="nins">
            <ComputationRail
              attributes={attributes}
              selectedId={selected.id}
              search={this.state.search}
              sort={this.state.sort}
              onSelect={this.select}
              onSearch={(search) => this.setState({ search })}
              onSort={(sort) => this.setState({ sort })}
            />
            <div className="nins-main">
              <div className="nins-head">
                <span className="nins-title">{selected.name}</span>
                {/*
                  The raw column, whenever the label renamed it. A tree node's own
                  condition is written in the column ("maturity range in 3-pre-2000..."),
                  so without this the same field goes by two names on one page and nothing
                  connects them. Shown as code because that is what it is: the thing a
                  split condition will actually be written against.
                */}
                {selected.name !== selected.id && (
                  <code className="nins-column">{selected.id}</code>
                )}
                {/*
                  What the field measures, above the node's figures. Someone choosing a
                  split has to know what they are splitting on, and the title alone does
                  not say: "Technology Count (Bucketed)" is a name, not a meaning. Absent
                  for a tenant's own computation, and then the line simply is not there.
                */}
                {selected.description && (
                  <span className="nins-means">{selected.description}</span>
                )}
                <span className="nins-sub">
                  Node {nodeId}: {count(node.population)} leads,{' '}
                  {count(node.conversions)} conversions,{' '}
                  {pct(stats.baseline, 2)} average
                </span>
              </div>
              {this.renderNotices(stats)}
              {this.renderVerdict(selected)}
              <BucketLadder
                stats={stats}
                expanded={this.state.expanded}
                onExpand={() => this.setState({ expanded: true })}
                highlight={this.state.highlight}
              />
            </div>
          </div>
        );
      }
    }
  )
);
